import dayjs, { type Dayjs } from 'dayjs';

export type PredefinedRangeLabel = '1m' | '1d' | '3m' | 'MTD' | 'QTD' | 'YTD' | 'All' | '7D' | '1y' | '0d';

export type PredefinedRange = {
  label: PredefinedRangeLabel;
  name: string;
  value: () => [Dayjs, Dayjs] | null;
};

export const rangeToday = {
  label: '0d',
  name: 'Today',
  value: (): [Dayjs, Dayjs] => [dayjs.utc(), dayjs.utc()],
} satisfies PredefinedRange;

export const rangeOneMonth = {
  label: '1m',
  name: 'Last 30 days',
  value: (): [Dayjs, Dayjs] => [dayjs.utc().subtract(30, 'day'), dayjs.utc()],
} satisfies PredefinedRange;

export const rangeOneDay = {
  label: '1d',
  name: 'Last 1 day',
  value: (): [Dayjs, Dayjs] => [dayjs.utc().subtract(1, 'day'), dayjs.utc()],
} satisfies PredefinedRange;

export const rangeThreeMonths = {
  label: '3m',
  name: 'Last 90 days',
  value: () => [dayjs.utc().subtract(90, 'day'), dayjs.utc()],
} satisfies PredefinedRange;

export const rangeLastYear = {
  label: '1y',
  name: 'Last 365 days',
  value: () => [dayjs.utc().subtract(365, 'day'), dayjs.utc()],
} satisfies PredefinedRange;

export const rangeMonthToDate = {
  label: 'MTD',
  name: 'Month to date',
  value: () => [dayjs.utc().startOf('month'), dayjs.utc()],
} satisfies PredefinedRange;

export const rangeQuarterToDate = {
  label: 'QTD',
  name: 'Quarter to date',
  value: () => [dayjs.utc().startOf('quarter'), dayjs.utc()],
} satisfies PredefinedRange;

export const rangeYearToDate = {
  label: 'YTD',
  name: 'Quarter to date',
  value: () => [dayjs.utc().startOf('year'), dayjs.utc()],
} satisfies PredefinedRange;

export const rangeAll: PredefinedRange = {
  label: 'All',
  name: 'All',
  value: () => null,
};

export const range7Days = {
  label: '7D',
  name: 'Last 7 days',
  value: () => [dayjs.utc().subtract(7, 'day'), dayjs.utc()],
} satisfies PredefinedRange;

export const defaultCalendarDateRanges: PredefinedRange[] = [
  rangeOneDay,
  range7Days,
  rangeOneMonth,
  rangeThreeMonths,
  rangeLastYear,
  rangeAll,
];

export const defaultQuickAccessDateRanges: PredefinedRange[] = [
  rangeOneDay,
  range7Days,
  rangeMonthToDate,
  rangeQuarterToDate,
  rangeYearToDate,
  rangeAll,
];

export const getDefaultRange = (): PredefinedRange => (dayjs.utc().date() < 7 ? range7Days : rangeMonthToDate);

const allRanges = [
  rangeOneMonth,
  rangeOneDay,
  rangeThreeMonths,
  rangeLastYear,
  rangeMonthToDate,
  rangeQuarterToDate,
  rangeYearToDate,
  rangeAll,
  range7Days,
];

const labelToRangeMap = new Map<PredefinedRangeLabel, PredefinedRange>(allRanges.map((range) => [range.label, range]));

export const getRangeByLabel = (label: PredefinedRangeLabel): PredefinedRange | undefined => {
  return labelToRangeMap.get(label);
};
